/**
 * IT — universal banner and navigation tracking for GA4.
 *
 * Three events, fired for anything on the site that qualifies, so a new
 * banner or a new menu link is measured without a new tag:
 *
 *   banner_impression  a banner has been at least half visible for a moment
 *   banner_click       a banner was clicked
 *   menu_click         a navigation link was clicked, desktop or mobile
 *
 * Delivery is `gtag('event', …)` when the Google tag is present — the store
 * loads GA4 as G-SM54S0RVFP through gtag.js — and a `dataLayer.push` when it
 * is not, so a GTM-only setup still receives everything under the same
 * names. If neither exists the module does nothing rather than throwing.
 *
 * WHAT COUNTS AS A BANNER
 * Two ways in, checked in this order:
 *
 *   1. `data-it-banner` on the element. This is the contract for anything
 *      new: add the attribute and it is tracked. `data-it-banner-name` and
 *      `data-it-banner-id` override the derived name and id.
 *   2. BANNER_SELECTORS below, which covers the banner surfaces the theme
 *      ships with today so nothing had to be edited to start measuring.
 *
 * A section whose type is in BANNER_SECTION_TYPES but which matched neither
 * is counted once as a whole, so a banner is never silently untracked — it
 * just reports at section granularity until someone marks it up.
 *
 * Everything is derived from the DOM. No section was modified to emit this,
 * which is what keeps it universal: it cannot fall out of step with markup
 * it does not depend on.
 */

/* The theme's own banner surfaces. Anything a customer would call a banner:
   a picture that leads somewhere. Ordered most specific first. */
const BANNER_SELECTORS = [
  '[data-it-banner]',
  '.slide__banner-link',
  '.every-terrain__card',
  '.grid-collection__card',
  '.horizontal-multiple-banner__slide',
  '.summer-edit-banner__card',
  '.layered-slideshow__slide',
  '.media-with-content__media',
  '.image-content__media',
  '.collection-links__card',
  '.bento-box a',
].join(',');

/* Nothing inside the navigation is ever a banner impression. The mega menu
   and the drawer carry promo tiles that look exactly like banners, and
   counting them would report an impression every time someone opened a
   menu — impressions inflated by navigation, measuring browsing rather
   than exposure. Their clicks are not lost: NAV_RULES matches first, so a
   promo tile reports as menu_click with nav_level "promo". */
const NAV_CONTAINERS = '#header-component, .menu-drawer-container, .shopify-section-group-header-group';

/* Sections that are banners even when nothing inside them matched above. */
const BANNER_SECTION_TYPES = [
  'slideshow',
  'hero',
  'horizontal_multiple_banner',
  'summer_edit_banner',
  'layered_slideshow',
  'image_content',
  'media_with_content',
  'grid_collection',
  'every_terrian',
  'collection_links',
];

/* Navigation surfaces, each mapped to the area and level it represents.
   FIRST MATCH WINS, so the drawer comes before the desktop menu: the drawer
   lives inside #header-component and its promo deck reuses the mega menu's
   own classes, so a desktop-first order filed every mobile promo click as
   a desktop one. */
const NAV_RULES = [
  // Mobile drawer
  { selector: '.menu-drawer__menu-item--mainlist', area: 'mobile_drawer', level: 'top' },
  { selector: '.menu-drawer__menu-item--parent', area: 'mobile_drawer', level: 'group' },
  { selector: '.menu-drawer__menu-item--child', area: 'mobile_drawer', level: 'child' },
  { selector: '.it-drawer-deck a', area: 'mobile_drawer', level: 'promo' },
  { selector: '.it-drawer-help__row a', area: 'mobile_drawer', level: 'help' },
  { selector: '.it-drawer-help__cta', area: 'mobile_drawer', level: 'help_cta' },
  { selector: '.mobile-menu-secondary a', area: 'mobile_drawer', level: 'utility' },
  { selector: '.menu-drawer-container .mega-menu__promo-tile', area: 'mobile_drawer', level: 'promo' },
  { selector: '.menu-drawer-container a', area: 'mobile_drawer', level: 'link' },

  // Desktop menu
  { selector: '#header-component .menu-list__link', area: 'desktop_menu', level: 'top' },
  { selector: '#header-component .mega-menu__link--parent', area: 'desktop_menu', level: 'group' },
  { selector: '#header-component .mega-menu__link', area: 'desktop_menu', level: 'child' },
  { selector: '#header-component .mega-menu__promo-tile', area: 'desktop_menu', level: 'promo' },

  // The icon row and the footer
  { selector: '#header-component .header-actions__action', area: 'header_actions', level: 'utility' },
  { selector: '.brand-footer a', area: 'footer', level: 'link' },
];

/* Half the banner on screen for this long before it counts as seen. Below
   about half a second, a fast scroll past registers as a view of everything
   it went by, which makes impressions meaningless. */
const DWELL_MS = 600;
const VISIBLE_RATIO = 0.5;

/* Several thresholds, not one: the callback has to run as a banner crosses
   the screen, and with a lone 0.5 a banner taller than the viewport never
   reaches it — the full-bleed hero fired nothing at all. */
const THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1];

/**
 * @param {string} name - GA4 event name
 * @param {Record<string, unknown>} params - Event parameters
 */
function send(name, params) {
  const payload = clean(params);

  if (window.ITAnalyticsDebug) console.info('[it-analytics]', name, payload);

  if (typeof window.gtag === 'function') {
    window.gtag('event', name, payload);
    return;
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: name, ...payload });
  }
}

/**
 * GA4 drops empty parameters anyway; removing them here keeps the debug
 * output readable and the payload small.
 * @param {Record<string, unknown>} params
 * @returns {Record<string, unknown>}
 */
function clean(params) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value != null));
}

/** @param {string} value @returns {string} Collapsed, trimmed, capped for GA4's 100-char limit. */
function text(value) {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
}

/**
 * Shopify names a section `template--123__slideshow_XeAcpN`. The type is the
 * middle: everything after `__`, minus the random suffix the editor appends
 * when a section is duplicated.
 * @param {Element | null} section
 * @returns {string}
 */
function sectionType(section) {
  const id = section?.id?.replace(/^shopify-section-/, '') ?? '';
  const afterTemplate = id.split('__').pop() ?? '';
  return afterTemplate.replace(/_[A-Za-z0-9]{6}$/, '');
}

/** @param {Element} el @returns {Element | null} */
function sectionOf(el) {
  return el.closest('.shopify-section, [id^="shopify-section"]');
}

/**
 * The most human name available: an explicit one, then a heading, then the
 * image's alt text, then the link's own words.
 * @param {Element} el
 * @returns {string}
 */
function bannerName(el) {
  const explicit = el.getAttribute('data-it-banner-name');
  if (explicit) return text(explicit);

  // Skip headings that are there for screen readers only — the slideshow
  // carries a visually-hidden "Slideshow" label, which every hero would
  // otherwise report as its name.
  const headings = el.querySelectorAll('h1, h2, h3, h4, [class*="title"], [class*="heading"]');
  for (const heading of headings) {
    if (heading.closest('.visually-hidden, .sr-only, [hidden]')) continue;
    if (heading.textContent?.trim()) return text(heading.textContent);
  }

  const alt = usable(el.querySelector('img[alt]')?.getAttribute('alt'));
  if (alt) return alt;

  const label = usable(el.getAttribute('aria-label')) || usable(el.textContent);
  if (label) return label;

  // A bare image with no words of its own — a full-bleed editorial banner,
  // say. Its section's heading is the only name a report could act on.
  const sectionHeading = usable(sectionOf(el)?.querySelector('h1, h2, h3')?.textContent);
  return sectionHeading || 'untitled';
}

/**
 * A name worth sending. Rejects blanks and Shopify's "Translation missing:"
 * placeholder, which is what an unset accessibility string renders as and
 * which would otherwise become the reported name of a banner.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function usable(value) {
  const out = text(value ?? '');
  return /^translation missing/i.test(out) ? '' : out;
}

/** @param {Element} el @returns {string} */
function bannerUrl(el) {
  const link = el.matches('a[href]') ? el : el.querySelector('a[href]');
  return link?.getAttribute('href') ?? '';
}

/** @returns {string} `mobile` under 750px, matching the theme's own breakpoint. */
function device() {
  return window.innerWidth < 750 ? 'mobile' : 'desktop';
}

/**
 * Shopify publishes the template on every page; it is the cleanest way to
 * say where a banner was seen without parsing the path.
 * @returns {string}
 */
function pageType() {
  return window.ShopifyAnalytics?.meta?.page?.pageType || document.body?.dataset?.template || '';
}

/**
 * @param {Element} el - The banner
 * @param {number} index - 1-based position among banners on the page
 * @returns {Record<string, unknown>}
 */
function bannerParams(el, index) {
  const section = sectionOf(el);
  const type = sectionType(section);
  const slot = Number(el.dataset.itBannerSlot) || indexWithin(el, section);

  return {
    // What was shown
    banner_id: el.getAttribute('data-it-banner-id') || `${type || 'section'}:${slot}`,
    banner_name: bannerName(el),
    link_url: bannerUrl(el),

    // Where it lives, so a banner can be found again in the theme editor
    section_id: section?.id?.replace(/^shopify-section-/, '') || '',
    section_type: type,
    creative_slot: slot,
    position: index,

    // The context it was shown in
    page_type: pageType(),
    page_path: location.pathname,
    device: device(),
  };
}

/**
 * @param {Element} el
 * @param {Element | null} section
 * @returns {number} 1-based position of this banner among its section's
 */
function indexWithin(el, section) {
  if (!section) return 1;
  const siblings = [...section.querySelectorAll(BANNER_SELECTORS)];
  const at = siblings.indexOf(el);
  return at === -1 ? 1 : at + 1;
}

/** @returns {Element[]} Every banner on the page, de-duplicated and in document order. */
function findBanners() {
  const found = new Set(document.querySelectorAll(BANNER_SELECTORS));

  // A banner section that matched nothing still reports, at section level.
  for (const section of document.querySelectorAll('.shopify-section, [id^="shopify-section"]')) {
    if (!BANNER_SECTION_TYPES.includes(sectionType(section))) continue;
    if ([...found].some((el) => section.contains(el))) continue;
    found.add(section);
  }

  // Document order, not match order: `position` is meant to say how far
  // down the page a banner sits, and the section-level fallbacks are added
  // after the selector matches, so an unsorted set put the first slideshow
  // at position 23.
  //
  // Only what is actually rendered is ranked. The mega menu's promo tiles
  // are in the markup on every page and sit above the hero in document
  // order, so counting them put the first banner on the homepage at
  // position 11. They rejoin the ranking when the menu opens and the
  // re-scan picks them up.
  return [...found]
    .filter((el) => el.getAttribute('data-it-banner') !== 'off')
    .filter((el) => !el.closest(NAV_CONTAINERS))
    .filter(isRendered)
    .sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
}

/* ---------- impressions ---------- */

const seen = new WeakSet();
const timers = new WeakMap();

/**
 * Counts as on screen when half the banner is showing OR — for a banner
 * taller than the window, like a full-bleed hero — when it covers half the
 * window. A single ratio test cannot express the second case, and without
 * it the largest banners on the site are the ones that never report.
 * @param {IntersectionObserverEntry} entry
 * @returns {boolean}
 */
function isSeen(entry) {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= VISIBLE_RATIO) return true;
  return entry.intersectionRect.height >= window.innerHeight * VISIBLE_RATIO;
}

/**
 * Rendered, not merely present. The mega menu's promo tiles sit in the DOM
 * with a real box while the menu is shut, so an intersection test alone
 * reported them as seen on every page load.
 * @param {Element} el
 * @returns {boolean}
 */
function isRendered(el) {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkVisibilityCSS: true, checkOpacity: true });
  }
  return el.offsetParent !== null;
}

function watchBanners() {
  const banners = findBanners();
  if (banners.length === 0) return;

  const order = new Map(banners.map((el, i) => [el, i + 1]));

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target;

        if (!isSeen(entry)) {
          clearTimeout(timers.get(el));
          timers.delete(el);
          continue;
        }

        if (seen.has(el) || timers.has(el) || !isRendered(el)) continue;

        timers.set(
          el,
          setTimeout(() => {
            timers.delete(el);
            if (seen.has(el) || !isRendered(el)) return;
            seen.add(el);
            observer.unobserve(el);
            send('banner_impression', bannerParams(el, order.get(el) ?? 0));
          }, DWELL_MS)
        );
      }
    },
    { threshold: THRESHOLDS }
  );

  for (const el of banners) observer.observe(el);
  return observer;
}

/* ---------- clicks ---------- */

/**
 * One delegated listener on the document, in the capture phase: menus and
 * banners are added, removed and re-rendered constantly here (the drawer,
 * the mega menu, <results-list> swapping the grid), and a listener bound to
 * each element would have to be rebound every time.
 * @param {MouseEvent} event
 */
function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const nav = matchNav(target);
  if (nav) {
    send('menu_click', navParams(nav.el, nav.rule));
    return;
  }

  const banner = target.closest(BANNER_SELECTORS);
  if (!banner || banner.getAttribute('data-it-banner') === 'off') return;
  if (banner.closest(NAV_CONTAINERS)) return;

  const banners = findBanners();
  send('banner_click', bannerParams(banner, banners.indexOf(banner) + 1));
}

/**
 * @param {Element} target
 * @returns {{el: Element, rule: typeof NAV_RULES[number]} | null}
 */
function matchNav(target) {
  for (const rule of NAV_RULES) {
    const el = target.closest(rule.selector);
    if (el) return { el, rule };
  }
  return null;
}

/**
 * @param {Element} el - The link
 * @param {typeof NAV_RULES[number]} rule
 * @returns {Record<string, unknown>}
 */
function navParams(el, rule) {
  const item = el.querySelector('[class*="text"], [class*="title"]')?.textContent || el.getAttribute('aria-label') || el.textContent;

  // The top-level menu this sits under, so "Shirts" can be told apart from
  // the "Shirts" in another column.
  const group = el.closest('[data-menu-handle]');
  const column = el.closest('.mega-menu__column, .mobile-menu-accordion__item, .menu-drawer__submenu');
  const parent =
    group?.getAttribute('data-menu-handle') ||
    text(column?.querySelector('.mega-menu__link--parent, .menu-drawer__title, h6')?.textContent) ||
    '';

  const peers = [...document.querySelectorAll(rule.selector)];

  return {
    nav_area: rule.area,
    nav_level: rule.level,
    nav_item: text(item) || 'untitled',
    nav_parent: text(parent),
    nav_position: peers.indexOf(el) + 1,
    link_url: el.getAttribute('href') || '',
    page_type: pageType(),
    page_path: location.pathname,
    device: device(),
  };
}

/* ---------- start ---------- */

function start() {
  document.addEventListener('click', onClick, { capture: true, passive: true });
  watchBanners();

  // Sections re-render on facet changes, cart updates and theme-editor
  // edits. Re-scan on a settle rather than per mutation, and let the
  // WeakSet keep already-counted banners from counting twice.
  let rescan = 0;
  new MutationObserver(() => {
    clearTimeout(rescan);
    rescan = setTimeout(watchBanners, 500);
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
